import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth';
import { useLang } from '../../lib/lang';
import { getSettings, updateSettings, updateAdminPassword, uploadFile, adminGetGoogleSettings, adminUpdateGoogleSettings } from '../../lib/api';
import { refreshBranding } from '../../lib/branding';
import AdminSidebar from '../../components/AdminSidebar';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Save, Lock, MessageSquare, Share2, Palette, Upload, RotateCcw, LogIn, CheckCircle2 } from 'lucide-react';

export default function AdminSettings() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flags, setFlags] = useState({});
  const [passwordForm, setPasswordForm] = useState({ old_password: '', new_password: '' });
  const [whatsapp, setWhatsapp] = useState({ enabled: false, number: '', message: '' });
  const [savingWa, setSavingWa] = useState(false);
  const [socialLinks, setSocialLinks] = useState({
    instagram: { enabled: false, url: '' },
    facebook: { enabled: false, url: '' },
    twitter: { enabled: false, url: '' },
    tiktok: { enabled: false, url: '' },
    youtube: { enabled: false, url: '' },
    linkedin: { enabled: false, url: '' },
  });
  const [savingSocial, setSavingSocial] = useState(false);
  const [branding, setBranding] = useState({
    logo_main_url: '',
    logo_small_url: '',
    favicon_url: '',
    site_title: '',
  });
  const [savingBranding, setSavingBranding] = useState(false);
  const [uploadingField, setUploadingField] = useState(null);
  const [certificate, setCertificate] = useState({ url: '', enabled: true, verification_url: '' });
  const [savingCert, setSavingCert] = useState(false);
  const [googleCfg, setGoogleCfg] = useState({ client_id: '', client_secret: '', redirect_uri: '', has_secret: false });
  const [savingGoogle, setSavingGoogle] = useState(false);

  const loadGoogle = async () => {
    try { const r = await adminGetGoogleSettings(); setGoogleCfg({ ...r.data, client_secret: '' }); } catch {}
  };
  const handleSaveGoogle = async () => {
    setSavingGoogle(true);
    try {
      const payload = { client_id: googleCfg.client_id, redirect_uri: googleCfg.redirect_uri };
      if (googleCfg.client_secret) payload.client_secret = googleCfg.client_secret;
      const r = await adminUpdateGoogleSettings(payload);
      setGoogleCfg({ ...r.data, client_secret: '' });
      toast.success(r.data.enabled ? 'Connexion Google activée ✓' : 'Enregistré (incomplet)');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    finally { setSavingGoogle(false); }
  };

  useEffect(() => { if (authLoading) return; if (!user || !isAdmin) { navigate('/admin/login'); return; } loadSettings(); loadGoogle(); }, [user, isAdmin, navigate, authLoading]);
  const loadSettings = async () => {
    try {
      const res = await getSettings();
      setSettings(res.data.settings);
      setFlags(res.data.settings?.feature_flags || {});
      setWhatsapp({
        enabled: res.data.settings?.whatsapp_enabled === true,
        number: res.data.settings?.whatsapp_number || '',
        message: res.data.settings?.whatsapp_message || '',
      });
      const sl = res.data.settings?.social_links || {};
      setSocialLinks((prev) => ({
        instagram: { enabled: sl.instagram?.enabled === true, url: sl.instagram?.url || '' },
        facebook:  { enabled: sl.facebook?.enabled === true,  url: sl.facebook?.url || '' },
        twitter:   { enabled: sl.twitter?.enabled === true,   url: sl.twitter?.url || '' },
        tiktok:    { enabled: sl.tiktok?.enabled === true,    url: sl.tiktok?.url || '' },
        youtube:   { enabled: sl.youtube?.enabled === true,   url: sl.youtube?.url || '' },
        linkedin:  { enabled: sl.linkedin?.enabled === true,  url: sl.linkedin?.url || '' },
        contact_email: sl.contact_email || '',
        contact_address: sl.contact_address || '',
      }));
      const br = res.data.settings?.branding || {};
      setBranding({
        logo_main_url: br.logo_main_url || '',
        logo_small_url: br.logo_small_url || '',
        favicon_url: br.favicon_url || '',
        site_title: br.site_title || '',
      });
      setCertificate({
        url: res.data.settings?.random_draw_certificate_url || '',
        enabled: res.data.settings?.random_draw_certificate_enabled !== false,
        verification_url: res.data.settings?.random_draw_verification_url || '',
      });
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  const handleSaveWhatsapp = async () => {
    setSavingWa(true);
    try {
      const cleanNumber = whatsapp.number.replace(/\D/g, '');
      await updateSettings({
        whatsapp_enabled: whatsapp.enabled,
        whatsapp_number: cleanNumber,
        whatsapp_message: whatsapp.message,
      });
      toast.success('WhatsApp ' + t('toastSaved'));
      setWhatsapp({ ...whatsapp, number: cleanNumber });
    } catch { toast.error(t('toastFailed')); }
    finally { setSavingWa(false); }
  };
  const handleSaveSocial = async () => {
    setSavingSocial(true);
    try {
      await updateSettings({ social_links: socialLinks });
      toast.success(t('socialLinksSaved') || 'Social links saved');
    } catch { toast.error(t('toastFailed')); }
    finally { setSavingSocial(false); }
  };

  const handleUploadBranding = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(t('fileTooBig') || 'Max 5MB'); return; }
    setUploadingField(field);
    try {
      const r = await uploadFile(file);
      const url = r.data.url;
      setBranding((prev) => ({ ...prev, [field]: url }));
      toast.success(t('uploadSuccess') || 'Image uploaded');
    } catch { toast.error(t('toastUploadFailed')); }
    finally { setUploadingField(null); e.target.value = ''; }
  };

  const handleSaveBranding = async () => {
    setSavingBranding(true);
    try {
      await updateSettings({ branding });
      await refreshBranding();
      toast.success(t('brandingSaved') || 'Branding saved & applied');
    } catch { toast.error(t('toastFailed')); }
    finally { setSavingBranding(false); }
  };

  const handleResetBranding = async (field) => {
    setBranding((prev) => ({ ...prev, [field]: '' }));
  };

  // ── Random draw certificate (homepage transparency badge) ──
  const handleSaveCertificate = async () => {
    setSavingCert(true);
    try {
      await updateSettings({
        random_draw_certificate_url: certificate.url || '',
        random_draw_certificate_enabled: certificate.enabled,
        random_draw_verification_url: (certificate.verification_url || '').trim(),
      });
      toast.success('Certificat sauvegardé');
    } catch (e) {
      toast.error(e?.response?.data?.detail || (t('toastFailed') || 'Erreur'));
    } finally {
      setSavingCert(false);
    }
  };

  const handleUploadCertificate = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingField('certificate');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('winluxor_token')}` },
        body: fd,
      });
      const data = await res.json();
      if (data?.url) {
        setCertificate((prev) => ({ ...prev, url: data.url }));
        toast.success('Image téléchargée');
      } else {
        toast.error('Échec upload');
      }
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploadingField(null);
      e.target.value = '';
    }
  };
  const handleSaveFlags = async () => {
    try {
      await updateSettings({ feature_flags: flags });
      toast.success(t('toastSaved'));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail.map(d => `${d.loc?.slice(-1)[0]||''}: ${d.msg}`).join(' · ') : e?.message || t('toastFailed'));
      toast.error(`Erreur sauvegarde : ${msg}`);
      console.error('[saveFlags] error:', e);
    }
  };
  const handleChangePassword = async () => { if (!passwordForm.old_password || !passwordForm.new_password) { toast.error(t('bothFieldsRequired') || 'Both fields required'); return; } try { await updateAdminPassword(passwordForm); toast.success(t('passwordUpdated') || 'Password updated'); setPasswordForm({ old_password: '', new_password: '' }); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  if (!user || !isAdmin) return null;

  const flagLabels = { raffles: t('activeDrawsSection'), packs: t('pointPacksSection'), winners: t('winnersSection'), urgency: t('urgencySection'), how_it_works: t('howItWorksSection'), trust_section: t('trustSection'), faq: t('faqSection'), show_tickets: t('showTicketsSold') };

  const navFlagLabels = { nav_competitions: t('navCompetitions'), nav_how_it_works: t('navHowItWorks'), nav_winners: t('navWinnersMenu'), nav_vip: t('navVip') };

  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      <main className="flex-1 p-6 lg:p-8">
        <div className="mb-8"><h1 className="font-display text-3xl font-bold tracking-tight">{t('settings')}</h1><p className="text-muted-foreground mt-1 text-sm">{t('settingsDesc')}</p></div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          <Card className="bg-white border-border lg:col-span-2" data-testid="admin-branding">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Palette className="w-4 h-4 text-gold" /> {t('brandingTitle') || 'Site Branding'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{t('brandingHelp') || 'Customize logos, favicon and browser tab title. Changes apply instantly.'}</p>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Site title */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold flex items-center gap-2">
                  {t('brandingTitleLabel') || 'Browser tab title'}
                </Label>
                <Input
                  value={branding.site_title}
                  onChange={(e) => setBranding({ ...branding, site_title: e.target.value })}
                  placeholder="WINLUXOR — Enter Your Dream"
                  className="rounded-xl"
                  data-testid="branding-title-input"
                />
                <p className="text-xs text-muted-foreground">{t('brandingTitleHelp') || 'Shown in the browser tab next to the favicon. Leave empty for default.'}</p>
              </div>

              {/* Logos grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                  { key: 'logo_main_url',  label: t('brandingMainLogo')  || 'Main logo (header)',     defaultUrl: '/assets/winluxor-logo.png', recommended: '500×250 PNG (transparent OK)', preview: 'h-20 w-auto', bg: 'bg-rolex-green' },
                  { key: 'logo_small_url', label: t('brandingSmallLogo') || 'Small logo (scrolled)',  defaultUrl: '/assets/winluxor-icon.png', recommended: 'Square 64×64 PNG',              preview: 'h-12 w-12 object-cover rounded-md', bg: 'bg-rolex-green' },
                  { key: 'favicon_url',    label: t('brandingFavicon')   || 'Favicon (browser tab)',  defaultUrl: '/assets/winluxor-icon.png', recommended: 'Square 32×32 PNG/ICO',          preview: 'h-8 w-8 object-cover rounded',     bg: 'bg-white' },
                ].map((item) => {
                  const currentUrl = branding[item.key] || item.defaultUrl;
                  const isCustom = !!branding[item.key];
                  return (
                    <div key={item.key} className="rounded-xl border border-[hsl(var(--gold)/0.25)] p-4 bg-[hsl(43_30%_94%)]">
                      <Label className="text-sm font-semibold block mb-2">{item.label}</Label>
                      <div className={`flex items-center justify-center rounded-lg ${item.bg} p-3 mb-3 min-h-[80px]`}>
                        {currentUrl ? (
                          <img src={currentUrl} alt="" className={item.preview} />
                        ) : (
                          <span className="text-xs text-muted-foreground">No image</span>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground mb-3 leading-tight">{item.recommended}</p>
                      <div className="flex gap-2">
                        <label className="flex-1 cursor-pointer">
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleUploadBranding(e, item.key)}
                            data-testid={`branding-upload-${item.key}`}
                          />
                          <Button
                            type="button"
                            asChild
                            size="sm"
                            variant="outline"
                            className="w-full rounded-lg gap-1.5 text-xs border-[hsl(var(--gold)/0.4)] cursor-pointer"
                            disabled={uploadingField === item.key}
                          >
                            <span>
                              <Upload className="w-3.5 h-3.5" />
                              {uploadingField === item.key ? t('uploading') || 'Uploading...' : (t('upload') || 'Upload')}
                            </span>
                          </Button>
                        </label>
                        {isCustom && (
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="rounded-lg gap-1 text-xs"
                            onClick={() => handleResetBranding(item.key)}
                            data-testid={`branding-reset-${item.key}`}
                            title={t('resetToDefault') || 'Reset to default'}
                          >
                            <RotateCcw className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                className="w-full bg-primary text-primary-foreground btn-press gap-2 rounded-xl"
                onClick={handleSaveBranding}
                disabled={savingBranding}
                data-testid="save-branding-button"
              >
                <Save className="w-4 h-4" /> {savingBranding ? (t('saving') || 'Saving...') : (t('saveAndApply') || 'Save & Apply')}
              </Button>
            </CardContent>
          </Card>

          {/* Random Draw Certificate */}
          <Card className="bg-white border-border" data-testid="admin-certificate-form">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                🎖️ Certificat de tirage aléatoire (accueil)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-xs text-muted-foreground">
                Affiché sur la page d'accueil dans un cadre or premium pour rassurer les visiteurs sur l'équité des tirages. Image idéale : capture du certificat randomdraws.com ou équivalent.
              </p>

              <div className="flex items-center justify-between p-3 rounded-xl bg-cream border border-gold/20">
                <Label className="font-bold">Afficher la section</Label>
                <Switch
                  checked={certificate.enabled}
                  onCheckedChange={(v) => setCertificate({ ...certificate, enabled: v })}
                  data-testid="cert-enabled-switch"
                />
              </div>

              <div className="space-y-2">
                <Label>URL de l'image du certificat</Label>
                <Input
                  value={certificate.url}
                  onChange={(e) => setCertificate({ ...certificate, url: e.target.value })}
                  placeholder="https://example.com/certificat.jpg ou téléchargez ci-dessous"
                  className="font-mono text-xs"
                  data-testid="cert-url-input"
                />
                <div className="flex gap-2">
                  <Label htmlFor="cert-upload" className="flex-1 cursor-pointer">
                    <div className="px-3 py-2 rounded-lg border-2 border-dashed border-gold/40 bg-cream text-center text-xs text-rolex-green/75 hover:border-gold/70 hover:bg-gold/5 transition-colors">
                      {uploadingField === 'certificate' ? '⏳ Téléchargement…' : '📤 Cliquez pour télécharger une image'}
                    </div>
                    <input id="cert-upload" type="file" accept="image/*" onChange={handleUploadCertificate} className="hidden" />
                  </Label>
                  {certificate.url && (
                    <Button variant="outline" size="sm" onClick={() => setCertificate({ ...certificate, url: '' })} className="text-red-600">
                      Effacer
                    </Button>
                  )}
                </div>
              </div>

              {certificate.url && (
                <div className="rounded-xl bg-cream border border-gold/20 p-3">
                  <p className="text-[10px] uppercase tracking-wider text-rolex-green/55 font-bold mb-2">Aperçu</p>
                  <img src={certificate.url} alt="Certificate preview" className="max-h-64 w-auto mx-auto rounded-lg border border-gold/20" />
                </div>
              )}

              <Button
                onClick={handleSaveCertificate}
                disabled={savingCert}
                className="w-full bg-primary text-primary-foreground btn-press gap-2 rounded-xl"
                data-testid="save-certificate-btn"
              >
                <Save className="w-4 h-4" /> {savingCert ? 'Sauvegarde…' : 'Sauvegarder'}
              </Button>

              {/* Random.org / Rdraws verification URL */}
              <div className="space-y-2 pt-3 mt-3 border-t border-border" data-testid="cert-verification-url-block">
                <Label className="font-semibold text-foreground">Lien de vérification du tirage</Label>
                <Input
                  type="url"
                  value={certificate.verification_url || ''}
                  onChange={(e) => setCertificate({ ...certificate, verification_url: e.target.value })}
                  placeholder="https://rdraws.com/rrtngnpe"
                  className="rounded-xl"
                  data-testid="cert-verification-url-input"
                />
                <p className="text-xs text-muted-foreground leading-relaxed">
                  URL publique du tirage (ex : rdraws.com). Quand renseignée, un bouton « Vérifier mon ticket » apparaîtra sous le certificat sur la page d'accueil — les visiteurs pourront cliquer dessus, saisir leur numéro de ticket et confirmer qu'il fait bien partie du tirage. Laissez vide pour ne rien afficher.
                </p>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-border" data-testid="admin-feature-flags-form"><CardHeader><CardTitle className="text-lg">{t('homepageSections')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(flagLabels).map(([key, label]) => (<div key={key} className="flex items-center justify-between"><Label className="text-sm">{label}</Label><Switch checked={flags[key] !== false} onCheckedChange={(v) => setFlags({...flags, [key]: v})} /></div>))}
              <Button className="w-full bg-primary text-primary-foreground btn-press gap-2 mt-4 rounded-xl" onClick={handleSaveFlags}><Save className="w-4 h-4" /> {t('saveSettings')}</Button>
            </CardContent>
          </Card>
          <Card className="bg-white border-border"><CardHeader><CardTitle className="text-lg">{t('navigationMenu')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {Object.entries(navFlagLabels).map(([key, label]) => (<div key={key} className="flex items-center justify-between"><Label className="text-sm">{label}</Label><Switch checked={flags[key] !== false} onCheckedChange={(v) => setFlags({...flags, [key]: v})} /></div>))}
              <Button className="w-full bg-primary text-primary-foreground btn-press gap-2 mt-4 rounded-xl" onClick={handleSaveFlags}><Save className="w-4 h-4" /> {t('saveSettings')}</Button>
            </CardContent>
          </Card>
          <Card className="bg-white border-border lg:col-span-2" data-testid="admin-google-oauth">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <LogIn className="w-4 h-4" /> Connexion Google
                {googleCfg.client_id && googleCfg.has_secret && googleCfg.redirect_uri && (
                  <span className="ml-2 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><CheckCircle2 className="w-4 h-4" /> Activée</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground leading-relaxed">
                Colle ici tes identifiants Google (créés sur Google Cloud Console) pour activer la connexion / inscription avec Google. Une fois les 3 champs remplis et enregistrés, le bouton « Continuer avec Google » s'active automatiquement sur le site.
              </p>
              <div className="space-y-2">
                <Label>ID client (Client ID)</Label>
                <Input value={googleCfg.client_id} onChange={(e) => setGoogleCfg({ ...googleCfg, client_id: e.target.value })} placeholder="1094…apps.googleusercontent.com" className="rounded-xl font-mono text-xs" data-testid="google-client-id" />
              </div>
              <div className="space-y-2">
                <Label>Code secret client (Client Secret)</Label>
                <Input type="password" value={googleCfg.client_secret} onChange={(e) => setGoogleCfg({ ...googleCfg, client_secret: e.target.value })} placeholder={googleCfg.has_secret ? '•••••••• (déjà enregistré — laisse vide pour garder)' : 'GOCSPX-…'} className="rounded-xl font-mono text-xs" data-testid="google-client-secret" />
              </div>
              <div className="space-y-2">
                <Label>URI de redirection autorisée</Label>
                <Input value={googleCfg.redirect_uri} onChange={(e) => setGoogleCfg({ ...googleCfg, redirect_uri: e.target.value })} placeholder="https://www.winluxor.com/auth/google" className="rounded-xl font-mono text-xs" data-testid="google-redirect-uri" />
                <p className="text-xs text-muted-foreground">⚠️ Doit être <strong>exactement</strong> la même URL que celle enregistrée dans Google Cloud Console (rubrique « URI de redirection autorisés »). Ex : <code>https://www.winluxor.com/auth/google</code></p>
              </div>
              <Button className="w-full bg-primary text-primary-foreground btn-press rounded-xl" onClick={handleSaveGoogle} disabled={savingGoogle} data-testid="google-save-button">
                <Save className="w-4 h-4 mr-2" /> {savingGoogle ? 'Enregistrement…' : 'Enregistrer Google'}
              </Button>
            </CardContent>
          </Card>
          <Card className="bg-white border-border"><CardHeader><CardTitle className="text-lg flex items-center gap-2"><Lock className="w-4 h-4" /> {t('changeAdminPassword')}</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2"><Label>{t('currentPassword')}</Label><Input type="password" value={passwordForm.old_password} onChange={(e) => setPasswordForm({...passwordForm, old_password: e.target.value})} className="rounded-xl" /></div>
              <div className="space-y-2"><Label>{t('newPassword')}</Label><Input type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})} className="rounded-xl" /></div>
              <Button className="w-full bg-primary text-primary-foreground btn-press rounded-xl" onClick={handleChangePassword}>{t('updatePassword')}</Button>
            </CardContent>
          </Card>
          <Card className="bg-white border-border"><CardHeader><CardTitle className="text-lg">{t('platformInfo')}</CardTitle></CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="flex justify-between items-center"><span className="text-muted-foreground">{t('currency')}</span><span className="font-mono font-semibold">GBP (£)</span></div>
              <div className="space-y-2">
                <Label className="font-semibold text-foreground">{t('pointRateLabel')}</Label>
                <p className="text-xs text-muted-foreground">{t('pointRateHelp')}</p>
                <div className="flex items-center gap-2">
                  <Input type="number" min="0.1" step="0.1" value={settings?.point_rate || 1} onChange={async (e) => { try { await updateSettings({ point_rate: parseFloat(e.target.value) || 1 }); toast.success(t('toastSaved')); const res = await getSettings(); setSettings(res.data.settings); } catch {} }} className="rounded-xl w-24 font-mono" />
                  <span className="text-muted-foreground text-xs">{t('pointsPerPound')}</span>
                </div>
                <p className="text-xs text-primary font-medium">{t('currentRate')}: {settings?.point_rate || 1} {t('points')} = £1</p>
              </div>
              <div className="flex justify-between items-center"><span className="text-muted-foreground">{t('adminUsername')}</span><span className="font-mono font-semibold">Admin</span></div>
              <div className="border-t border-border pt-4 space-y-3">
                <h4 className="font-semibold text-foreground">{t('signupBonus')}</h4>
                <Input type="number" value={settings?.signup_bonus || 0} onChange={async (e) => { try { await updateSettings({ signup_bonus: parseInt(e.target.value) || 0 }); toast.success(t('toastSaved')); const res = await getSettings(); setSettings(res.data.settings); } catch {} }} className="rounded-xl w-32" />
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-foreground">{t('referralBonusPoints')}</h4>
                <p className="text-xs text-muted-foreground">{t('referralBonusHelp')}</p>
                <Input type="number" value={settings?.referral_bonus || 10} onChange={async (e) => { try { await updateSettings({ referral_bonus: parseInt(e.target.value) || 0 }); toast.success(t('toastSaved')); const res = await getSettings(); setSettings(res.data.settings); } catch {} }} className="rounded-xl w-32" />
              </div>
              <div className="border-t border-border pt-4 space-y-3" data-testid="admin-welcome-offer">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-foreground flex items-center gap-2">🎁 {t('welcomeOfferTitle') || 'Offre de bienvenue'}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5">{t('welcomeOfferHelp') || 'Remise auto sur la 1ʳᵉ participation (1 fois par compte).'}</p>
                  </div>
                  <Switch checked={!!settings?.welcome_offer?.enabled}
                    onCheckedChange={async (v) => { try { await updateSettings({ welcome_offer: { ...(settings?.welcome_offer || {}), enabled: v } }); toast.success(t('toastSaved')); const r = await getSettings(); setSettings(r.data.settings); } catch {} }}
                    data-testid="welcome-offer-enabled" />
                </div>
                <div className="flex items-end gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">{t('welcomeOfferAmount') || 'Montant (£)'}</Label>
                    <Input type="number" min="0" step="1" defaultValue={settings?.welcome_offer?.amount ?? 10}
                      onBlur={async (e) => { try { await updateSettings({ welcome_offer: { ...(settings?.welcome_offer || {}), amount: parseFloat(e.target.value) || 0 } }); toast.success(t('toastSaved')); const r = await getSettings(); setSettings(r.data.settings); } catch {} }}
                      className="rounded-xl w-28 font-mono" data-testid="welcome-offer-amount" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">{t('welcomeOfferMin') || 'Achat min (£)'}</Label>
                    <Input type="number" min="0" step="1" defaultValue={settings?.welcome_offer?.min_spend ?? 0}
                      onBlur={async (e) => { try { await updateSettings({ welcome_offer: { ...(settings?.welcome_offer || {}), min_spend: parseFloat(e.target.value) || 0 } }); toast.success(t('toastSaved')); const r = await getSettings(); setSettings(r.data.settings); } catch {} }}
                      className="rounded-xl w-28 font-mono" data-testid="welcome-offer-min" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white border-border lg:col-span-2" data-testid="admin-social-links">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Share2 className="w-4 h-4 text-gold" /> {t('socialMediaLinks') || 'Social Media Links (Footer)'}
              </CardTitle>
              <p className="text-xs text-muted-foreground mt-1">{t('socialMediaHelp') || 'Configure URLs and visibility of social icons in the footer'}</p>
            </CardHeader>
            <CardContent className="space-y-3">
              {[
                { key: 'instagram', label: 'Instagram',   placeholder: 'https://instagram.com/winluxor' },
                { key: 'facebook',  label: 'Facebook',    placeholder: 'https://facebook.com/winluxor' },
                { key: 'twitter',   label: 'Twitter / X', placeholder: 'https://x.com/winluxor' },
                { key: 'tiktok',    label: 'TikTok',      placeholder: 'https://tiktok.com/@winluxor' },
                { key: 'youtube',   label: 'YouTube',     placeholder: 'https://youtube.com/@winluxor' },
                { key: 'linkedin',  label: 'LinkedIn',    placeholder: 'https://linkedin.com/company/winluxor' },
              ].map((p) => (
                <div key={p.key} className="flex items-center gap-3 p-3 rounded-xl border border-[hsl(var(--gold)/0.2)] bg-[hsl(43_30%_94%)]">
                  <div className="w-24 shrink-0">
                    <Label className="text-sm font-semibold">{p.label}</Label>
                  </div>
                  <Input
                    value={socialLinks[p.key]?.url || ''}
                    onChange={(e) => setSocialLinks({ ...socialLinks, [p.key]: { ...socialLinks[p.key], url: e.target.value } })}
                    placeholder={p.placeholder}
                    className="rounded-lg flex-1 h-9 text-sm"
                    data-testid={`social-${p.key}-url`}
                  />
                  <Switch
                    checked={socialLinks[p.key]?.enabled === true}
                    onCheckedChange={(v) => setSocialLinks({ ...socialLinks, [p.key]: { ...socialLinks[p.key], enabled: v } })}
                    data-testid={`social-${p.key}-toggle`}
                  />
                </div>
              ))}

              {/* Contact email + address — visible in footer */}
              <div className="pt-4 mt-2 border-t border-stone-200 space-y-3">
                <p className="text-xs uppercase tracking-wider font-bold text-gold-dark">📞 Coordonnées (affichées dans le footer)</p>
                <div className="space-y-2">
                  <Label className="text-sm">📧 Email de contact</Label>
                  <Input
                    type="email"
                    value={socialLinks.contact_email || ''}
                    onChange={(e) => setSocialLinks({ ...socialLinks, contact_email: e.target.value })}
                    placeholder="support@winluxor.com"
                    className="rounded-xl"
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm">📍 Adresse postale</Label>
                  <Input
                    value={socialLinks.contact_address || ''}
                    onChange={(e) => setSocialLinks({ ...socialLinks, contact_address: e.target.value })}
                    placeholder="123 Luxury Lane, London W1, UK"
                    className="rounded-xl"
                    data-testid="contact-address-input"
                  />
                  <p className="text-[11px] text-muted-foreground">💡 Adresse affichée dans le footer du site, à côté de l'email.</p>
                </div>
              </div>

              <Button
                className="w-full bg-primary text-primary-foreground btn-press gap-2 mt-4 rounded-xl"
                onClick={handleSaveSocial}
                disabled={savingSocial}
                data-testid="save-social-button"
              >
                <Save className="w-4 h-4" /> {savingSocial ? (t('saving') || 'Saving...') : t('saveSettings')}
              </Button>
            </CardContent>
          </Card>

          <Card className="bg-white border-border" data-testid="admin-whatsapp-config">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="w-4 h-4 text-[#25D366]" /> WhatsApp Badge
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-sm font-semibold">{t('whatsappEnableLabel') || 'Enable WhatsApp badge'}</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">{t('whatsappEnableHelp') || 'Show floating WhatsApp button on left side of the site'}</p>
                </div>
                <Switch
                  checked={whatsapp.enabled}
                  onCheckedChange={(v) => setWhatsapp({ ...whatsapp, enabled: v })}
                  data-testid="whatsapp-enable-switch"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('whatsappNumberLabel') || 'WhatsApp number (with country code)'}</Label>
                <Input
                  value={whatsapp.number}
                  onChange={(e) => setWhatsapp({ ...whatsapp, number: e.target.value })}
                  placeholder="+44 7700 900000"
                  className="rounded-xl font-mono"
                  data-testid="whatsapp-number-input"
                />
                <p className="text-xs text-muted-foreground">{t('whatsappNumberHelp') || 'Use international format. Spaces and dashes are removed automatically.'}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">{t('whatsappMessageLabel') || 'Pre-filled message (optional)'}</Label>
                <Input
                  value={whatsapp.message}
                  onChange={(e) => setWhatsapp({ ...whatsapp, message: e.target.value })}
                  placeholder="Hi! I have a question about WINLUXOR."
                  className="rounded-xl"
                  data-testid="whatsapp-message-input"
                />
              </div>
              <Button
                className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white btn-press gap-2 mt-2 rounded-xl"
                onClick={handleSaveWhatsapp}
                disabled={savingWa}
                data-testid="whatsapp-save-button"
              >
                <Save className="w-4 h-4" /> {savingWa ? t('saving') || 'Saving...' : t('saveSettings')}
              </Button>
            </CardContent>
          </Card>

        </div>
      </main>
    </div>
  );
}
